import {
  QuicHandshakeRejection,
  QuicOwnedProbeFailure,
  QuicTransport,
  type TransportEndpoint,
} from "./webtransport";
import { RttTracker } from "./network-timing";
import { inputPackets, INPUT_HISTORY_LIMIT } from "./input-packets";
import { FrameDecoder } from "./frame-decoder";
import type { PlayerInput } from "./types";
export const PROTOCOL_VERSION = 9;
const UPDATE_REQUIRED = {
  type: "error",
  code: "client_update_required",
  message: "The arena has been updated. Refresh this page to continue.",
};
export interface Session {
  token: string;
  playerId: number;
  name: string;
}
export type ServerMessage = Record<string, any>;
export class Connection {
  private socket?: WebSocket;
  private quic?: QuicTransport;
  private retry?: ReturnType<typeof setTimeout>;
  private heartbeat?: ReturnType<typeof setInterval>;
  private inputRetry?: ReturnType<typeof setTimeout>;
  private pendingInputs: { input: PlayerInput; at: number }[] = [];
  private matchId?: string;
  private closed = false;
  private attempts = 0;
  private generation = 0;
  private connecting = false;
  private session?: Session;
  private initializing?: Promise<Session>;
  private messageChain: Promise<void> = Promise.resolve();
  private readonly frames = new FrameDecoder(decodeFrame);
  private lastReceived = 0;
  private timing = new RttTracker();
  private authenticated = false;
  private blockingError?: string;
  // Only a welcomed, subsequently closed startup probe can reserve this
  // identity before WS fallback. A new tab never gets this retry allowance.
  private probeReleaseDeadline?: number;
  private probeReleaseAttempts = 0;
  get blockedReason(): string | undefined {
    return this.blockingError;
  }
  transportKind: "websocket" | "webtransport" | null = null;
  onMessage: (event: ServerMessage, raw: string, receivedAt: number) => void = () => {};
  onTiming: (rttMs: number | null, sampleMs: number | null) => void = () => {};
  onStatus: (
    status: "connecting" | "connected" | "reconnecting" | "offline" | "blocked",
  ) => void = () => {};
  async initialize(): Promise<Session> {
    if (this.session) {
      if (
        !this.connecting &&
        !this.quic &&
        (!this.socket || this.socket.readyState === WebSocket.CLOSED)
      ) {
        clearTimeout(this.retry);
        void this.connect();
      }
      return this.session;
    }
    if (this.initializing) return this.initializing;
    this.initializing = this.createSession().finally(() => {
      this.initializing = undefined;
    });
    return this.initializing;
  }
  private async createSession(): Promise<Session> {
    let token: string | undefined;
    try {
      token = localStorage.getItem("warlock.token") ?? undefined;
    } catch {
      /* ephemeral guest */
    }
    const response = await fetch("/api/session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
      signal: AbortSignal.timeout(10_000),
    });
    if (!response.ok)
      throw new Error("The arena is unavailable. Please try again.");
    const session = (await response.json()) as Session;
    this.session = session;
    try {
      localStorage.setItem("warlock.token", session.token);
    } catch {
      /* ephemeral guest */
    }
    void this.connect();
    return session;
  }
  private hello(): unknown {
    return {
      type: "hello",
      protocol: PROTOCOL_VERSION,
      token: this.session!.token,
      ...("DecompressionStream" in globalThis ? { compression: "gzip" } : {}),
    };
  }
  private async connect(): Promise<void> {
    if (
      this.closed ||
      !this.session ||
      this.connecting ||
      this.quic ||
      this.socket?.readyState === WebSocket.CONNECTING ||
      this.socket?.readyState === WebSocket.OPEN
    )
      return;
    this.connecting = true;
    const generation = ++this.generation;
    this.resetFrames();
    this.resetTiming();
    this.onStatus(this.attempts ? "reconnecting" : "connecting");
    // A failed endpoint probe is bounded and never prevents the reliable fallback.
    if (QuicTransport.available() && this.probeReleaseDeadline === undefined) {
      try {
        const response = await fetch("/api/transport", {
          signal: AbortSignal.timeout(1200),
        });
        if (response.ok) {
          const config = (await response.json()) as {
            webtransport: TransportEndpoint | null;
          };
          if (config.webtransport) {
            const quic = await QuicTransport.open(
              config.webtransport,
              this.hello(),
              (data) => this.receive(data, generation),
              () => this.disconnected(generation),
            );
            if (this.closed || generation !== this.generation) {
              quic.close();
              return;
            }
            this.quic = quic;
            this.transportKind = "webtransport";
            this.connecting = false;
            this.startHeartbeat(generation);
            quic.activate();
            return;
          }
        }
      } catch (error) {
        if (error instanceof QuicHandshakeRejection) {
          // open() already closed the rejected probe. Do not authenticate a
          // second fallback transport for a server-level ownership denial.
          this.connecting = false;
          this.receive(error.frame, generation);
          return;
        }
        if (error instanceof QuicOwnedProbeFailure) {
          this.probeReleaseDeadline = performance.now() + 3000;
          this.probeReleaseAttempts = 0;
        }
        /* Unsupported, blocked UDP or failed datagram probe: use WebSocket. */
      }
    }
    if (this.closed || generation !== this.generation) return;
    const url = new URL("/ws", location.href);
    url.protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = (this.socket = new WebSocket(url));
    socket.binaryType = "arraybuffer";
    this.transportKind = "websocket";
    socket.onopen = () => {
      if (this.socket !== socket || generation !== this.generation) return;
      this.connecting = false;
      this.send(this.hello());
      this.startHeartbeat(generation);
    };
    socket.onmessage = (event) => this.receive(event.data, generation);
    socket.onclose = () => this.disconnected(generation);
    socket.onerror = () => socket.close();
  }
  private receive(data: string | ArrayBuffer, generation: number): void {
    const receivedAt = performance.now();
    if (generation !== this.generation || this.closed) return;
    const decoded = this.frames.enqueue(
      data,
      () => generation === this.generation && !this.closed,
    );
    this.messageChain = this.messageChain
      .then(async () => {
        if (generation !== this.generation || this.closed) return;
        const result = await decoded;
        if (generation !== this.generation || this.closed) return;
        if (result.kind === "discarded") return;
        if (result.kind === "error") throw result.error;
        let raw = result.raw;
        let message = JSON.parse(raw);
        if (
          message.type === "welcome" &&
          message.protocol !== PROTOCOL_VERSION
        ) {
          message = UPDATE_REQUIRED;
          raw = JSON.stringify(message);
        }
        if (
          message.type === "error" &&
          message.code === "session_in_use" &&
          this.transportKind === "websocket" &&
          this.probeReleaseDeadline !== undefined &&
          performance.now() < this.probeReleaseDeadline
        ) {
          // close() is not a remote-release acknowledgment. Observe admission
          // again with bounded backoff; never evict the existing server owner.
          const socket = this.socket;
          const delay = Math.min(
            100 * 2 ** this.probeReleaseAttempts++,
            500,
            this.probeReleaseDeadline - performance.now(),
          );
          this.disconnected(generation, delay);
          socket?.close();
          return;
        }
        if (
          message.type === "error" &&
          ["client_update_required", "session_in_use"].includes(message.code)
        ) {
          if (message.code === "session_in_use")
            this.blockingError = message.message;
          // Close before UI callbacks: a rejected tab may cancel a pending
          // entry, but must never send leave/actions for the established owner.
          this.close();
          this.onMessage(message, raw, receivedAt);
          this.onStatus(
            message.code === "session_in_use" ? "blocked" : "offline",
          );
          return;
        }
        this.lastReceived = receivedAt;
        if (message.type === "pong") {
          const rtt = this.timing.receive(message.nonce, receivedAt);
          if (rtt === undefined) return;
          this.onTiming(rtt, this.timing.sampleMs ?? null);
        }
        if (message.type === "match") {
          this.clearInputs();
          this.matchId = message.matchId;
          this.quic?.setMatch(message.matchId);
        }
        if (message.type === "welcome") {
          this.probeReleaseDeadline = undefined;
          this.probeReleaseAttempts = 0;
          this.attempts = 0;
          this.authenticated = true;
          this.onStatus("connected");
          this.measureRtt();
        }
        this.onMessage(message, raw, receivedAt);
      })
      .catch((error) => console.warn("Ignored malformed server event", error));
  }
  private resetFrames(): void {
    // A retired connection cannot hold the next connection's dispatch behind
    // an obsolete in-flight decoder. Active work still shares the four-worker
    // limit; queued obsolete frames are discarded before decoding starts.
    this.messageChain = Promise.resolve();
    this.frames.discardStale();
  }
  private startHeartbeat(generation: number): void {
    this.lastReceived = performance.now();
    clearInterval(this.heartbeat);
    this.heartbeat = setInterval(() => {
      if (performance.now() - this.lastReceived > 20_000) {
        this.quic?.close();
        this.socket?.close();
        this.disconnected(generation);
        return;
      }
      if (this.authenticated) this.measureRtt();
    }, 1000);
  }
  private measureRtt(): void {
    const nonce = this.timing.request(performance.now());
    if (!this.send({ type: "ping", nonce })) this.timing.cancel(nonce);
  }
  private resetTiming(): void {
    this.authenticated = false;
    this.timing.reset();
    this.onTiming(null, null);
  }
  private disconnected(generation: number, retryDelay?: number): void {
    if (this.closed || generation !== this.generation) return;
    ++this.generation;
    this.resetFrames();
    this.connecting = false;
    this.quic = undefined;
    this.socket = undefined;
    this.clearInputs();
    this.matchId = undefined;
    this.resetTiming();
    clearInterval(this.heartbeat);
    this.onStatus("reconnecting");
    const delay = retryDelay ?? Math.min(8000, 500 * 2 ** this.attempts);
    ++this.attempts;
    this.retry = setTimeout(() => void this.connect(), delay);
  }
  send(command: unknown): boolean {
    if (this.closed) return false;
    const type = (command as { type?: string } | null)?.type;
    const epoch = (command as { matchId?: string } | null)?.matchId;
    if (
      (type === "input" || type === "input_bundle") &&
      epoch !== undefined &&
      epoch !== this.matchId
    )
      return false;
    if (type === "input")
      return this.sendInputs([(command as { input: PlayerInput }).input]);
    if (type === "input_bundle")
      return this.sendInputs((command as { inputs: PlayerInput[] }).inputs);
    if (
      ["leave", "matchmake", "create_room", "join_room"].includes(type ?? "")
    ) {
      this.clearInputs();
      this.matchId = undefined;
    }
    if (this.quic) return this.quic.send(command);
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(
      typeof command === "string" ? command : JSON.stringify(command),
    );
    return true;
  }
  sendInputs(inputs: PlayerInput[]): boolean {
    if (this.closed) return false;
    if (!inputs.length) return true;
    if (!this.matchId) return false;
    if (this.quic) return this.quic.sendInputs(inputs);
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    const at = performance.now();
    this.pendingInputs.push(...inputs.map((input) => ({ input, at })));
    this.pendingInputs = this.pendingInputs.slice(-INPUT_HISTORY_LIMIT);
    this.flushInputs();
    return true;
  }
  private flushInputs(): void {
    clearTimeout(this.inputRetry);
    this.inputRetry = undefined;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    const now = performance.now();
    this.pendingInputs = this.pendingInputs.filter(({ at }) => now - at <= 200);
    if (!this.pendingInputs.length) return;
    // TCP bytes already submitted cannot be replaced. Bound the browser queue
    // and retain only a recent input window while the socket is congested.
    if (this.socket.bufferedAmount > 1024) {
      this.inputRetry = setTimeout(() => this.flushInputs(), 16);
      return;
    }
    const inputs = this.pendingInputs.map(({ input }) => input);
    this.pendingInputs = [];
    for (const packet of inputPackets(inputs, undefined, this.matchId))
      this.socket.send(new TextDecoder().decode(packet));
  }
  private clearInputs(): void {
    clearTimeout(this.inputRetry);
    this.inputRetry = undefined;
    this.pendingInputs = [];
  }
  close(): void {
    this.closed = true;
    this.probeReleaseDeadline = undefined;
    ++this.generation;
    this.resetFrames();
    this.resetTiming();
    clearTimeout(this.retry);
    clearInterval(this.heartbeat);
    this.clearInputs();
    this.matchId = undefined;
    this.quic?.close();
    this.socket?.close();
  }
}
/** Large snapshots may be gzip, smaller messages remain raw. The connection
 * dispatches results in delivery order after bounded concurrent decoding. */
export async function decodeFrame(data: string | ArrayBuffer): Promise<string> {
  if (typeof data === "string") return data;
  const bytes = new Uint8Array(data);
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
    const stream = new Blob([data])
      .stream()
      .pipeThrough(new DecompressionStream("gzip"));
    return new Response(stream).text();
  }
  return new TextDecoder().decode(bytes);
}
